import { User } from "Domain/User/Entity/User";
import { GridColDef } from "@mui/x-data-grid";
import SearchTable from "pages/components/Molecules/SearchTable";
import { USER_SEARCH } from "Application/RequestTypes";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import FormFields from "pages/components/FormFields";
import Button from "pages/components/FormFields/Button";
import AddIcon from "@mui/icons-material/Add";
import { eventDispatcher } from "pages/Hooks/useEventListener";
import { TRIGGER_DIALOG, TRIGGER_MODAL } from "pages/constants/EventKeys";
import {
  deleteInternalUser,
  getCurrentUserData,
  impersonateUser,
} from "Application/User/UserService";
import { useTranslation } from "react-i18next";
import { Box, Grid2, Typography } from "@mui/material";
import { useState } from "react";
import { FormFieldsConfig } from "pages/components/Form/FormEntities";
import useForm from "pages/components/Form/useForm";
import { CreateInternalUserRequest } from "Domain/User/Dto/CreateInternalUserRequest";
import { CreateInternalUser } from "Application/User/RegisterService";
import UserTypeChip from "pages/components/Status/UserTypeChip";
import { getUrl, HOME } from "Infrastructure/config/routesConfig";

export default function InternalUsersList() {
  const currentUser = getCurrentUserData();
  const [refresh, setRefresh] = useState(true);
  const { t } = useTranslation();

  const columns: GridColDef<User>[] = [
    {
      field: "email",
      width: 90,
      flex: 1,
    },
    {
      field: "type",
      flex: 1,
      renderCell: (params) => {
        return <UserTypeChip status={params.row.type} />;
      },
    },
    {
      field: "createdAt",
      flex: 1,
      type: "string",
    },
    {
      field: "active",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "grid",
            justifyContent: "center",
            alignContent: "center",
            height: "100%",
          }}
        >
          {params.row.isVerified ? (
            <VerifiedUserIcon color="success" />
          ) : (
            <GppMaybeIcon color="warning" />
          )}
        </Box>
      ),
    },
  ];

  if (currentUser.isUserAdmin()) {
    columns.push({
      field: "action",
      flex: 1,
      headerAlign: "center",
      align: "center",

      sortable: false,
      renderCell: (params) => {
        function handleUserImpersonate() {
          impersonateUser(params.row.publicId).then((res: any) => {
            if (res && res === "OK") {
              window.location.href = window.location.origin + getUrl(HOME);
            }
          });
        }

        return (
          currentUser.userData.publicId !== params.row.publicId && (
            <>
              <Button
                label={t("FORM.SUBMIT.delete")}
                color="error"
                size="small"
                onClick={() => {
                  DeleteUserModal(params.row.publicId);
                }}
              />
              <Button
                size="small"
                label={"impersonate"}
                onClick={handleUserImpersonate}
              />
            </>
          )
        );
      },
    });
  }

  function DeleteUserModal(id: string) {
    function handleClick() {
      deleteInternalUser(id).then((res) => {
        setRefresh(false);
        setTimeout(() => {
          setRefresh(true);
        }, 0);
      });
    }
    const actions = [
      {
        label: "delete",
        variant: "outlined",
        color: "error",
        action: handleClick,
      },
    ];

    eventDispatcher(TRIGGER_DIALOG, {
      type: "delete_internal_user",
      actions: actions,
    });
  }

  function addNewInternalUser() {
    eventDispatcher(TRIGGER_MODAL, {
      title: "create_internal_user",
      content: <AddNewUserForm />,
    });
  }

  return (
    <>
      <Box display={"flex"} justifyContent={"end"} paddingBottom={1}>
        {currentUser.isUserAdmin() && (
          <Button
            color="success"
            onClick={addNewInternalUser}
            startIcon={<AddIcon />}
            label={"Nouvel Utilisateur"}
          />
        )}
      </Box>

      {refresh && (
        <SearchTable<User>
          columns={columns}
          additionalFilters={{
            type: "ADMIN||MANAGER||FINANCIAL",
          }}
          endpoint={USER_SEARCH}
        />
      )}
    </>
  );
}

function AddNewUserForm() {
  const { t } = useTranslation();
  const AddInternalUserFormConfig: FormFieldsConfig = {
    email: {
      constraints: ["email", "required"],
    },
    type: {
      constraints: ["required"],
      options: [
        {
          label: "manager",
          value: "MANAGER",
        },
        {
          label: "financial",
          value: "FINANCIAL",
        },
      ],
    },
  };
  const {
    errors,
    values,
    options,
    handleInputChange,
    isValidForm,
    formatErrorObject,
    Form,
  } = useForm(AddInternalUserFormConfig, true);

  function handleSubmit(e: any) {
    if (isValidForm()) {
      CreateInternalUser(getCreateInternalUserRequest(values)).then(
        (res: any) => {
          if (!res.error) {
            eventDispatcher(TRIGGER_MODAL, {
              title: "internal_user_created_title",
              content: (
                <Typography>
                  {t(`FEEDBACK.MODAL.internal_user_created_text`)}
                </Typography>
              ),
              actions: [
                {
                  label: "ok",
                  action: () => {},
                },
              ],
            });
          }
        }
      );
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Grid2 display={"grid"} gridTemplateColumns={"1fr"} gap={2}>
        <FormFields.Email
          autoFocus
          name="email"
          placeholder="enter email"
          value={values.email}
          onChange={handleInputChange}
          required
          {...formatErrorObject(errors.email)}
        />
        <FormFields.Select
          name="type"
          value={values.type}
          onChange={handleInputChange}
          options={options.type}
          required
          {...formatErrorObject(errors.type)}
        />
      </Grid2>
      <FormFields.Button
        type="submit"
        label={t("FORM.SUBMIT.create")}
        fullWidth
        sx={{ mt: 2 }}
      />
    </Form>
  );
}

function getCreateInternalUserRequest(values: any) {
  const createUserRequest = new CreateInternalUserRequest(
    values.email,
    values.type
  );

  return createUserRequest;
}
